import { useState, useRef, useEffect } from 'react';
import {
  FileText,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2
} from 'lucide-react';

interface TranscriptSegment {
  start: number;
  text: string;
}

interface VideoContentData {
  id: string;
  video_id: string;
  video_url: string;
  title: string;
  transcript: TranscriptSegment[] | null;
  summary: string | null;
  key_points: string[];
}

interface VideoLessonProps {
  videoContent: VideoContentData;
  mode: 'NO_TOOL' | 'OPEN_AI' | 'AUDIT';
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function VideoLesson({ videoContent, mode }: VideoLessonProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript'>('summary');
  const [expandedSummary, setExpandedSummary] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);

  const { video_id, title, transcript, summary, key_points } = videoContent;

  // In NO_TOOL mode, only show video and transcript (no AI summary)
  const showAiFeatures = mode !== 'NO_TOOL';

  // Seek to timestamp in video
  const seekTo = (seconds: number) => {
    if (playerRef.current?.contentWindow) {
      playerRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'seekTo',
          args: [seconds, true]
        }),
        '*'
      );
      setCurrentTime(seconds);
    }
  };

  // Listen for player state changes
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'infoDelivery' && data.info?.currentTime) {
          setCurrentTime(data.info.currentTime);
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Auto-scroll transcript to current position
  useEffect(() => {
    if (transcript && transcriptRef.current && activeTab === 'transcript') {
      const activeSegment = transcript.findIndex(
        (seg, i) => {
          const nextSeg = transcript[i + 1];
          return currentTime >= seg.start && (!nextSeg || currentTime < nextSeg.start);
        }
      );

      if (activeSegment >= 0) {
        const segmentEl = transcriptRef.current.querySelector(`[data-segment="${activeSegment}"]`);
        segmentEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentTime, transcript, activeTab]);

  return (
    <div className="space-y-6">
      {/* Video Player */}
      <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
        <iframe
          ref={playerRef}
          src={`https://www.youtube.com/embed/${video_id}?enablejsapi=1&origin=${window.location.origin}`}
          title={title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      {/* Video Title */}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {showAiFeatures && (
            <button
              onClick={() => setActiveTab('summary')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'summary'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              AI Summary
            </button>
          )}
          <button
            onClick={() => setActiveTab('transcript')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'transcript'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4" />
            Transcript
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Summary Tab */}
        {activeTab === 'summary' && showAiFeatures && (
          <div className="space-y-6">
            {/* Summary */}
            {summary && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
                <button
                  onClick={() => setExpandedSummary(!expandedSummary)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-500" />
                    Quick Summary
                  </h4>
                  {expandedSummary ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {expandedSummary && (
                  <p className="mt-4 text-gray-700 leading-relaxed whitespace-pre-line">
                    {summary}
                  </p>
                )}
              </div>
            )}

            {/* Key Points */}
            {key_points && key_points.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Key Takeaways</h4>
                <ul className="space-y-3">
                  {key_points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!summary && key_points?.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>AI summary not available for this video.</p>
              </div>
            )}
          </div>
        )}

        {/* Transcript Tab */}
        {activeTab === 'transcript' && (
          <div ref={transcriptRef} className="max-h-[400px] overflow-y-auto space-y-1 pr-2">
            {transcript && transcript.length > 0 ? (
              transcript.map((segment, index) => {
                const isActive = currentTime >= segment.start &&
                  (!transcript[index + 1] || currentTime < transcript[index + 1].start);

                return (
                  <button
                    key={index}
                    data-segment={index}
                    onClick={() => seekTo(segment.start)}
                    className={`w-full text-left p-3 rounded-lg transition-colors flex items-start gap-3 ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-900'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className={`flex-shrink-0 text-xs font-mono ${
                      isActive ? 'text-indigo-600' : 'text-gray-400'
                    }`}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatTime(segment.start)}
                    </span>
                    <span className="text-sm">{segment.text}</span>
                  </button>
                );
              })
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Transcript not available for this video.</p>
                <p className="text-sm mt-2">The video may not have captions enabled.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Loading skeleton for video content
export function VideoLessonSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="aspect-video bg-gray-200 rounded-lg" />
      <div className="h-6 bg-gray-200 rounded w-3/4" />
      <div className="border-b border-gray-200 pb-3">
        <div className="flex space-x-8">
          <div className="h-4 bg-gray-200 rounded w-24" />
          <div className="h-4 bg-gray-200 rounded w-24" />
        </div>
      </div>
      <div className="space-y-4">
        <div className="h-32 bg-gray-100 rounded-lg" />
        <div className="h-48 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

// Component to process video on first load
export function VideoProcessor({
  lessonId,
  videoUrl,
  onProcessed
}: {
  lessonId: string;
  videoUrl: string;
  onProcessed: (data: VideoContentData) => void;
}) {
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function processVideo() {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/video/process', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            lesson_id: lessonId,
            video_url: videoUrl
          })
        });

        if (!response.ok) {
          throw new Error('Failed to process video');
        }

        const data = await response.json();
        onProcessed(data.video_content);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process video');
      } finally {
        setProcessing(false);
      }
    }

    processVideo();
  }, [lessonId, videoUrl, onProcessed]);

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600 mb-4" />
        <p className="text-gray-600">Processing video content...</p>
        <p className="text-sm text-gray-400 mt-2">Fetching transcript and generating summary</p>
      </div>
    );
  }

  return null;
}
